import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { api, formatApiError } from "@/lib/api";
import { TAG_CHOICES } from "@/components/TagPicker";
import { WorkflowTable } from "@/components/WorkflowTable";
import RunDemoModal from "@/components/RunDemoModal";
import BulkFolderMoveBar from "@/components/BulkFolderMoveBar";
import FoldersSidebar, {
  SPECIAL_ALL,
  SPECIAL_UNFOLDERED,
} from "@/components/FoldersSidebar";
import WorkflowDragChip from "@/components/WorkflowDragChip";
import AISpendChip from "@/components/AISpendChip";

export default function WorkflowLibraryPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("active");
  const [tag, setTag] = useState("__all__");
  const [search, setSearch] = useState("");
  const [runTarget, setRunTarget] = useState(null);

  // Phase FOLDER-UX (FU.2) — sidebar selection.  null = All workflows;
  // SPECIAL_UNFOLDERED = orphan filter; any string = a real folder_id.
  const [selectedFolderId, setSelectedFolderId] = useState(null);

  // Phase FOLDER-UX (FU.3) — drag state.  `activeDrag` carries the
  // payload from useDraggable so the DragOverlay can render the chip.
  // `shiftHeldRef` is a ref because we don't want re-renders on every
  // keypress during a drag — we read it once in onDragEnd.
  const [activeDrag, setActiveDrag] = useState(null);
  const [shiftHeld, setShiftHeld] = useState(false);
  const shiftHeldRef = useRef(false);
  // Used by the confirm dialog for the "detach from all folders" path.
  const [detachConfirm, setDetachConfirm] = useState(null);

  // ---- query 1: workflows list (folder-aware filter) -------------
  const workflowsQuery = useQuery({
    queryKey: ["workflows", statusFilter, tag, search, selectedFolderId],
    queryFn: async () => {
      const params = { status: statusFilter, page: 1, page_size: 100 };
      if (tag && tag !== "__all__") params.tag = tag;
      if (search.trim()) params.search = search.trim();
      if (selectedFolderId && selectedFolderId !== SPECIAL_UNFOLDERED) {
        params.folder_id = selectedFolderId;
      } else if (selectedFolderId === SPECIAL_UNFOLDERED) {
        params.unfoldered = true;
      }
      const { data } = await api.get("/workflows", { params });
      return data;
    },
  });
  const { data, isLoading, isError, error } = workflowsQuery;

  // ---- query 2: sidebar totals (counts for All + Unfoldered) -----
  // Reuse the same `/workflows` endpoint but with page_size=1 to keep
  // payloads tiny — we only need the `total` field.
  const totalsAll = useQuery({
    queryKey: ["workflows-total", "all", statusFilter],
    queryFn: async () =>
      (await api.get("/workflows", {
        params: { status: statusFilter, page: 1, page_size: 1 },
      })).data,
    staleTime: 30_000,
  });
  const totalsUnfoldered = useQuery({
    queryKey: ["workflows-total", "unfoldered", statusFilter],
    queryFn: async () =>
      (await api.get("/workflows", {
        params: {
          status: statusFilter,
          page: 1,
          page_size: 1,
          unfoldered: true,
        },
      })).data,
    staleTime: 30_000,
  });

  // When the user clicks Run on a row, we have only the summary —
  // fetch the full detail so the modal can render the variable form.
  const runDetail = useQuery({
    queryKey: ["workflow", runTarget?.workflow_id],
    queryFn: async () =>
      (await api.get(`/workflows/${runTarget.workflow_id}`)).data,
    enabled: !!runTarget,
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ["workflows"] });
    queryClient.invalidateQueries({ queryKey: ["workflows-total"] });
    queryClient.invalidateQueries({ queryKey: ["folders-sidebar"] });
    queryClient.invalidateQueries({ queryKey: ["folder-summary-by-workflow"] });
    queryClient.invalidateQueries({ queryKey: ["workflow-folders"] });
  }

  const duplicateMutation = useMutation({
    mutationFn: async (wf) => {
      const { data } = await api.post(`/workflows/${wf.workflow_id}/duplicate`);
      return data;
    },
    onSuccess: (data) => {
      toast.success(`Duplicated as "${data.name}"`);
      invalidate();
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const archiveMutation = useMutation({
    mutationFn: async (wf) => {
      const { data } = await api.post(`/workflows/${wf.workflow_id}/archive`);
      return data;
    },
    onSuccess: () => {
      toast.success("Workflow archived");
      invalidate();
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const unarchiveMutation = useMutation({
    mutationFn: async (wf) => {
      const { data } = await api.post(`/workflows/${wf.workflow_id}/unarchive`);
      return data;
    },
    onSuccess: () => {
      toast.success("Workflow restored");
      invalidate();
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const items = data?.items ?? [];

  // R2-Phase-4 — multi-select for bulk folder ops on workflows.
  // Reset when filters change so we never act on stale rows.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  useEffect(() => {
    setSelectedIds(new Set());
  }, [statusFilter, tag, search, selectedFolderId]);

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const togglePageSelection = () => {
    setSelectedIds((prev) => {
      const visibleIds = items.map((w) => w.workflow_id);
      const allSelected =
        visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
      return allSelected ? new Set() : new Set(visibleIds);
    });
  };

  // ---- DnD plumbing ---------------------------------------------
  // Track Shift key during the drag.  We start/stop the listeners on
  // drag-start / drag-end to avoid a permanent global listener.
  useEffect(() => {
    if (!activeDrag) return undefined;
    function onKeyDown(e) {
      if (e.key === "Shift") {
        shiftHeldRef.current = true;
        setShiftHeld(true);
      }
    }
    function onKeyUp(e) {
      if (e.key === "Shift") {
        shiftHeldRef.current = false;
        setShiftHeld(false);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [activeDrag]);

  const sensors = useSensors(
    // 6px activation distance lets the row's checkbox + name link still
    // receive normal clicks — the pointer only enters "drag mode" once
    // you actually move 6px past the handle.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  // Add the dropped workflow(s) to the target folder.  Preserves
  // existing folder memberships.
  const addMutation = useMutation({
    mutationFn: async ({ folder_id, workflow_ids }) => {
      const { data } = await api.post(
        `/folders/${folder_id}/workflow-items`,
        { workflow_ids },
      );
      return data;
    },
    onSuccess: (resp, vars) => {
      const n = vars.workflow_ids.length;
      toast.success(
        `Added ${n} workflow${n === 1 ? "" : "s"} to “${vars.folder_name}”`,
      );
      invalidate();
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  // Atomic move — remove from every other folder + add to target.
  // Uses the backend's batch endpoint so the UI never sees a half-state.
  const moveMutation = useMutation({
    mutationFn: async ({ folder_id, workflow_ids }) => {
      const { data } = await api.post(
        `/folders/${folder_id}/move-workflows`,
        { workflow_ids },
      );
      return data;
    },
    onSuccess: (resp, vars) => {
      const n = vars.workflow_ids.length;
      toast.success(
        `Moved ${n} workflow${n === 1 ? "" : "s"} to “${vars.folder_name}”`,
      );
      invalidate();
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  // Detach from every folder — fired when the user drops on
  // "Unfoldered" or "All workflows" (the brief treats both as
  // detach-from-all targets) AFTER they confirm the destructive intent.
  const detachMutation = useMutation({
    mutationFn: async ({ workflow_ids }) => {
      // No bulk-detach endpoint — fan out the per-workflow folder list
      // and DELETE each membership.  Sequential per workflow to keep
      // the audit trail clean; parallel across workflows is fine.
      await Promise.all(
        workflow_ids.map(async (wid) => {
          const { data: f } = await api.get(`/workflows/${wid}/folders`);
          const folders = f?.folders || [];
          for (const folder of folders) {
            await api.delete(
              `/folders/${folder.folder_id}/workflow-items/${wid}`,
            );
          }
        }),
      );
    },
    onSuccess: (_resp, vars) => {
      const n = vars.workflow_ids.length;
      toast.success(
        `Removed ${n} workflow${n === 1 ? "" : "s"} from all folders`,
      );
      invalidate();
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const handleDragStart = useCallback((event) => {
    const data = event.active?.data?.current;
    if (data?.type === "workflow-handle") {
      setActiveDrag({
        workflow_id: data.workflow_id,
        workflow_name: data.workflow_name,
        selected_ids: data.selected_ids || [data.workflow_id],
      });
    }
  }, []);

  const handleDragCancel = useCallback(() => {
    setActiveDrag(null);
    setShiftHeld(false);
    shiftHeldRef.current = false;
  }, []);

  const handleDragEnd = useCallback(
    (event) => {
      const dragData = event.active?.data?.current;
      const dropData = event.over?.data?.current;
      const wfIds = dragData?.selected_ids || [];
      setActiveDrag(null);
      const wasShift = shiftHeldRef.current;
      setShiftHeld(false);
      shiftHeldRef.current = false;

      if (!dropData || !wfIds.length) return;

      if (dropData.type === "folder") {
        if (dropData.can_modify_items === false) {
          toast.error(
            `“${dropData.folder_name}” is read-only — you don't have permission to add workflows.`,
          );
          return;
        }
        if (wasShift) {
          moveMutation.mutate({
            folder_id: dropData.folder_id,
            folder_name: dropData.folder_name,
            workflow_ids: wfIds,
          });
        } else {
          addMutation.mutate({
            folder_id: dropData.folder_id,
            folder_name: dropData.folder_name,
            workflow_ids: wfIds,
          });
        }
      } else if (dropData.type === "special") {
        // Both "All workflows" and "Unfoldered" treat the drop as a
        // detach-from-everything action.  Always confirm before
        // committing — it's destructive.
        if (
          dropData.target === SPECIAL_ALL ||
          dropData.target === SPECIAL_UNFOLDERED
        ) {
          setDetachConfirm({ workflow_ids: wfIds });
        }
      }
    },
    [addMutation, moveMutation],
  );

  // ---- render ----------------------------------------------------
  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragCancel={handleDragCancel}
      onDragEnd={handleDragEnd}
    >
      <div
        className="flex flex-col gap-4 md:flex-row md:items-start"
        data-testid="workflow-library-page"
      >
        <FoldersSidebar
          selectedFolderId={selectedFolderId}
          onSelect={setSelectedFolderId}
          totals={{
            all: totalsAll.data?.total ?? null,
            unfoldered: totalsUnfoldered.data?.total ?? null,
          }}
        />

        <div className="min-w-0 flex-1 space-y-4">
          <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
                  Workflow Library
                </h1>
                {/* R3 Phase 6A.7.2 — 30-day AI-spend rollup chip.
                    Backend RBAC-gates: presenter/viewer see only their
                    own spend; admin/builder see tenant-wide. */}
                <AISpendChip />
              </div>
              <p className="mt-1 text-sm text-slate-500">
                Author and version the demo workflows the agent runs against Eyvo.
              </p>
            </div>
            <Button
              onClick={() => navigate("/workflows/new")}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="new-workflow-button"
            >
              <Plus size={16} className="mr-1.5" />
              New workflow
            </Button>
          </header>

          <FiltersBar
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            tag={tag}
            onTagChange={setTag}
            search={search}
            onSearchChange={setSearch}
            total={data?.total}
          />

          {isLoading ? (
            <div className="space-y-2" data-testid="workflow-table-loading">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full rounded-lg" />
              ))}
            </div>
          ) : isError ? (
            <div
              className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
              data-testid="workflow-table-error"
            >
              {formatApiError(error)}
            </div>
          ) : (
            <WorkflowTable
              items={items}
              onEdit={(wf) => navigate(`/workflows/${wf.workflow_id}/edit`)}
              onDuplicate={(wf) => duplicateMutation.mutate(wf)}
              onArchive={(wf) => archiveMutation.mutate(wf)}
              onUnarchive={(wf) => unarchiveMutation.mutate(wf)}
              onRun={(wf) => setRunTarget(wf)}
              selectedIds={selectedIds}
              onToggleSelected={toggleSelected}
              onTogglePageSelection={togglePageSelection}
              dndEnabled
            />
          )}
        </div>

        <RunDemoModal
          workflow={runDetail.data}
          open={!!runTarget && !!runDetail.data}
          onOpenChange={(o) => !o && setRunTarget(null)}
        />
        <BulkFolderMoveBar
          selectedIds={selectedIds}
          onClear={() => setSelectedIds(new Set())}
          targetType="workflow"
        />

        <AlertDialog
          open={!!detachConfirm}
          onOpenChange={(o) => !o && setDetachConfirm(null)}
        >
          <AlertDialogContent data-testid="detach-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Remove from all folders?</AlertDialogTitle>
              <AlertDialogDescription>
                This will remove{" "}
                {detachConfirm?.workflow_ids?.length === 1
                  ? "this workflow"
                  : `${detachConfirm?.workflow_ids?.length} workflows`}{" "}
                from every folder it currently lives in. The workflow itself
                is not deleted — it will appear under "Unfoldered" and stay
                in the main library.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="detach-confirm-cancel">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                className="bg-rose-600 hover:bg-rose-700"
                data-testid="detach-confirm-go"
                onClick={() => {
                  if (detachConfirm) {
                    detachMutation.mutate({
                      workflow_ids: detachConfirm.workflow_ids,
                    });
                  }
                  setDetachConfirm(null);
                }}
              >
                Remove from all
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <DragOverlay dropAnimation={null}>
        {activeDrag ? (
          <WorkflowDragChip
            workflowName={activeDrag.workflow_name}
            selectedCount={activeDrag.selected_ids?.length || 1}
            shiftHeld={shiftHeld}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

function FiltersBar({ statusFilter, onStatusChange, tag, onTagChange, search, onSearchChange, total }) {
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3 sm:flex-row sm:items-center">
      <div className="flex flex-wrap items-center gap-1" data-testid="status-filter-group">
        {["active", "archived", "all"].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onStatusChange(s)}
            data-testid={`status-filter-${s}`}
            className={[
              "rounded-md px-3 py-1.5 text-xs font-medium capitalize transition-colors",
              statusFilter === s
                ? "bg-indigo-600 text-white shadow-sm"
                : "text-slate-600 hover:bg-slate-100",
            ].join(" ")}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search
            size={14}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <Input
            placeholder="Search by name…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9"
            data-testid="workflow-search-input"
          />
        </div>
        <Select value={tag} onValueChange={onTagChange}>
          <SelectTrigger className="w-full sm:w-52" data-testid="tag-filter-trigger">
            <SelectValue placeholder="All tags" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All tags</SelectItem>
            {TAG_CHOICES.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {typeof total === "number" && (
        <div
          className="ml-auto text-[11px] uppercase tracking-wider text-slate-400"
          data-testid="workflow-total-count"
        >
          {total} result{total === 1 ? "" : "s"}
        </div>
      )}
    </div>
  );
}
